/**
 * Convocazione e formazione iniziale.
 *
 * Precede il timer: senza titolari l'avvio e bloccato, perche i pulsanti
 * evento stanno sotto il nome dei giocatori in campo e l'app deve sapere chi
 * sono prima che il cronometro parta. I convocati si salvano anche da soli,
 * giorni prima (V19): la partita resta programmata e i titolari si scelgono
 * il giorno della gara.
 *
 * La rosa sta in una card per reparto, una sotto l'altra a ogni larghezza, con i conteggi di convocati e
 * titolari accanto al titolo, in ordine di cognome o, con l'interruttore
 * «Overall» della barra, per overall: ogni riga è quella della
 * vista Rosa (`RigaGiocatore`: stati, minuti, voto e medie secondo i permessi)
 * e a destra, in colonna, la spunta del convocato e l'interruttore del
 * titolare. Sopra le card, in una barra che resta in vista, i conteggi,
 * «Convoca tutti» e i comandi a icona. Nessuna spunta di partenza oltre a
 * quelle gia salvate. Chi non gestisce la partita (l'Admin) legge la stessa
 * lista senza comandi.
 */

import { useEffect, useState } from 'react';
import {
  ArrowDownWideNarrowIcon,
  ClipboardCheckIcon,
  SaveIcon,
  ShirtIcon,
  TriangleAlert,
  TriangleAlertIcon,
  UsersIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { chiama } from '../api/client.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { Maglia } from '../comuni/Maglia.js';
import { BarraScheda, CardVista } from '../comuni/Riquadri.js';
import { ComandoIcona } from '../comuni/Azioni.js';
import { COLORE_STATO } from '../giocatori/colori-stato.js';
import {
  REPARTI,
  type RigaRosa,
  type RigaRosaOrdinabile,
  conMedie,
  repartoDi,
} from '../giocatori/elenchi.js';
import { RigaGiocatore } from '../giocatori/RigaGiocatore.js';
import { BadgeStato, CartellinoSportivo } from '../giocatori/Stati.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { avvisoPortieri } from './portieri.js';
import type { GiocatoreInRosa } from './tipi.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const confronto = new Intl.Collator('it', { sensitivity: 'base' });

export function Formazione({
  partitaId,
  squadraStagioneId,
  rosa,
  giocatoriInCampo,
  modifica,
  onConfermata,
}: {
  partitaId: string;
  squadraStagioneId: string;
  rosa: GiocatoreInRosa[];
  giocatoriInCampo: number;
  /** Mister e Dirigente scelgono; l'Admin legge. */
  modifica: boolean;
  onConfermata: () => void;
}) {
  const [convocati, setConvocati] = useState<Set<string>>(
    () => new Set(rosa.filter((g) => g.convocato).map((g) => g.id)),
  );
  const [titolari, setTitolari] = useState<Set<string>>(
    () => new Set(rosa.filter((g) => g.titolare).map((g) => g.id)),
  );
  const [altrove, setAltrove] = useState<Set<string>>(new Set());
  const [salvataggio, setSalvataggio] = useState(false);
  const [convocatiSalvati, setConvocatiSalvati] = useState(true);
  const [perOverall, setPerOverall] = useState(false);
  // Le righe della rosa: stato, minuti, voto e medie come nella vista Rosa.
  const { dati: righeRosa, errore: erroreRosa } = useDati<RigaRosa[]>(
    `/squadre-stagione/${squadraStagioneId}/rosa`,
  );

  // Chi e gia convocato altrove oggi. Non impedisce nulla: e la situazione
  // normale del ragazzo aggregato dalla categoria inferiore.
  useEffect(() => {
    const ids = [...convocati];
    if (ids.length === 0) return;
    let vivo = true;
    void chiama<{ giocatori: string[] }>(`/partite/${partitaId}/doppie-convocazioni`, {
      metodo: 'POST',
      corpo: { giocatori: ids },
    })
      .then((r) => vivo && setAltrove(new Set(r.giocatori)))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [convocati, partitaId]);

  const togliTitolare = (ids: Iterable<string>) =>
    setTitolari((t) => {
      const s = new Set(t);
      for (const id of ids) s.delete(id);
      return s;
    });

  const commutaConvocato = (id: string) => {
    setConvocatiSalvati(false);
    setConvocati((precedenti) => {
      const nuovi = new Set(precedenti);
      if (nuovi.has(id)) {
        nuovi.delete(id);
        togliTitolare([id]);
      } else {
        nuovi.add(id);
      }
      return nuovi;
    });
  };

  // Tutti convocati: si tolgono tutti, titolari compresi; altrimenti si convocano tutti.
  const tutti = convocati.size === rosa.length && rosa.length > 0;
  const commutaTutti = () => {
    setConvocatiSalvati(false);
    if (tutti) {
      setConvocati(new Set());
      setTitolari(new Set());
    } else {
      setConvocati(new Set(rosa.map((g) => g.id)));
    }
  };

  const commutaTitolare = (id: string) => {
    if (!convocati.has(id)) return;
    setTitolari((precedenti) => {
      const nuovi = new Set(precedenti);
      if (nuovi.has(id)) nuovi.delete(id);
      else if (nuovi.size < giocatoriInCampo) nuovi.add(id);
      return nuovi;
    });
  };

  const salva = async () => {
    setSalvataggio(true);
    try {
      await chiama(`/partite/${partitaId}/formazione`, {
        metodo: 'POST',
        corpo: { convocati: [...convocati], titolari: [...titolari] },
      });
      onConfermata();
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setSalvataggio(false);
    }
  };

  const salvaConvocati = async () => {
    setSalvataggio(true);
    try {
      await chiama(`/partite/${partitaId}/convocati`, {
        metodo: 'POST',
        corpo: { convocati: [...convocati] },
      });
      setConvocatiSalvati(true);
      toast.success(`${convocati.size} convocati salvati.`);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setSalvataggio(false);
    }
  };

  // Un giocatore puo avere due tesseramenti nella stessa rosa: la riga e comunque sua.
  const scheda = new Map((righeRosa ?? []).map((r) => [r.giocatoreId, conMedie(r)]));

  // Per cognome; a richiesta per overall, dal più alto, chi non ce l'ha in fondo.
  const perCognome = (a: GiocatoreInRosa, b: GiocatoreInRosa) =>
    confronto.compare(a.cognome, b.cognome) || confronto.compare(a.nome, b.nome);
  const overall = (g: GiocatoreInRosa) => scheda.get(g.id)?.overall ?? -Infinity;
  const reparti = REPARTI.map((_, i) =>
    rosa
      .filter((g) => repartoDi(g.ruolo) === i)
      .sort((a, b) => (perOverall ? overall(b) - overall(a) : 0) || perCognome(a, b)),
  );
  const conVoti = righeRosa?.some((r) => 'votoMedio' in r) ?? false;
  const conMedieAttributi = righeRosa?.some((r) => 'medieAttributi' in r) ?? false;

  const pronti = titolari.size === giocatoriInCampo;
  const completa = titolari.size >= giocatoriInCampo;
  // I portieri non bloccano nulla: il secondo puo giocare in campo. Senza
  // portiere si avvisa solo a titolari completi, prima e solo una scelta a meta.
  const ruoliTitolari = rosa.filter((g) => titolari.has(g.id)).map((g) => g.ruolo);
  const avviso = avvisoPortieri(ruoliTitolari, 'tra i titolari');
  const avvisoPortiere =
    avviso && (pronti || ruoliTitolari.some((r) => r === 'portiere')) ? avviso : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Resta in vista scorrendo la rosa, sotto la testata del guscio. */}
      <BarraScheda
        // Sporge dalle card quanto il loro anello: scorrendo non ne resta il bordo ai lati.
        className="sticky top-[var(--altezza-testata,0px)] z-10 -mx-2 -my-2 bg-background px-2 py-2"
        comandi={
          <>
            <Toggle
              variant="outline"
              pressed={perOverall}
              onPressedChange={setPerOverall}
              aria-label="Ordina per overall"
              title={perOverall ? 'Torna in ordine di cognome' : 'Ordina per overall'}
            >
              <ArrowDownWideNarrowIcon />
              Overall
            </Toggle>
            {modifica && (
              <>
                <ComandoIcona
                  etichetta="Salva solo i convocati"
                  motivo={convocatiSalvati ? 'Convocati salvati' : undefined}
                  Icona={SaveIcon}
                  variant="outline"
                  disabled={salvataggio || convocatiSalvati}
                  onClick={() => void salvaConvocati()}
                />
                <ComandoIcona
                  etichetta="Conferma la formazione"
                  motivo={
                    pronti
                      ? undefined
                      : `Scegli ancora ${giocatoriInCampo - titolari.size} titolari`
                  }
                  Icona={ClipboardCheckIcon}
                  variant="default"
                  disabled={!pronti || salvataggio}
                  onClick={() => void salva()}
                />
              </>
            )}
          </>
        }
      >
        {modifica && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="convoca-tutti"
              checked={tutti}
              onCheckedChange={commutaTutti}
              disabled={rosa.length === 0}
            />
            <Label htmlFor="convoca-tutti">Convoca tutti</Label>
          </div>
        )}
        <span className="flex items-center gap-1 text-muted-foreground" title="Convocati">
          <UsersIcon aria-hidden className="size-4" />
          <span className="font-semibold text-foreground tabular-nums">{convocati.size}</span>
          convocati
        </span>
        <span className="flex items-center gap-1 text-muted-foreground" title="Titolari">
          <ShirtIcon aria-hidden className="size-4" />
          <span className="font-semibold text-foreground tabular-nums">
            {titolari.size}/{giocatoriInCampo}
          </span>
          titolari
        </span>
        {avvisoPortiere && (
          <Badge role="status" className="bg-avviso text-avviso-foreground">
            <TriangleAlert />
            {avvisoPortiere}
          </Badge>
        )}
      </BarraScheda>

      {/* Si aspettano le righe prima di disegnare, altrimenti saltano di posto
        sotto il dito; se la lettura fallisce si sceglie lo stesso, con la
        riga essenziale. */}
      {erroreRosa && (
        <Alert>
          <TriangleAlertIcon />
          <AlertTitle>Dati dei giocatori non disponibili</AlertTitle>
          <AlertDescription>{erroreRosa}</AlertDescription>
        </Alert>
      )}
      {!righeRosa && !erroreRosa ? (
        <StatoCaricamento errore={null} />
      ) : (
        // Tooltip che aspettano un secondo anche passando da un controllo al
        // vicino, e che si chiudono appena il puntatore esce: sono solo testo.
        <TooltipProvider delayDuration={1000} skipDelayDuration={0} disableHoverableContent>
          <div className="flex flex-col gap-4">
            {reparti.map((gruppo, i) => (
              <CardVista
                key={REPARTI[i]}
                titolo={REPARTI[i]!}
                conteggio={
                  <>
                    <UsersIcon aria-label="Convocati" />
                    {gruppo.filter((g) => convocati.has(g.id)).length}/{gruppo.length}
                  </>
                }
                accanto={
                  <Badge variant="outline" className="bg-background tabular-nums">
                    <ShirtIcon aria-label="Titolari" />
                    {gruppo.filter((g) => titolari.has(g.id)).length}
                  </Badge>
                }
              >
                {gruppo.length === 0 ? (
                  <StatoVuoto titolo="Nessuno in rosa" />
                ) : (
                  <ul className="grid gap-2 @5xl:grid-cols-2">
                    {gruppo.map((g) => (
                      <RigaConvocato
                        key={g.id}
                        g={g}
                        riga={scheda.get(g.id)}
                        conVoti={conVoti}
                        conMedie={conMedieAttributi}
                        convocato={convocati.has(g.id)}
                        titolare={titolari.has(g.id)}
                        altrove={altrove.has(g.id)}
                        completa={completa}
                        giocatoriInCampo={giocatoriInCampo}
                        modifica={modifica}
                        onConvocato={() => commutaConvocato(g.id)}
                        onTitolare={() => commutaTitolare(g.id)}
                      />
                    ))}
                  </ul>
                )}
              </CardVista>
            ))}
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}

function RigaConvocato({
  g,
  riga,
  conVoti,
  conMedie,
  convocato,
  titolare,
  altrove,
  completa,
  giocatoriInCampo,
  modifica,
  onConvocato,
  onTitolare,
}: {
  g: GiocatoreInRosa;
  /** La riga della rosa; assente se la lettura non è riuscita. */
  riga: RigaRosaOrdinabile | undefined;
  conVoti: boolean;
  conMedie: boolean;
  convocato: boolean;
  titolare: boolean;
  altrove: boolean;
  completa: boolean;
  giocatoriInCampo: number;
  modifica: boolean;
  onConvocato: () => void;
  onTitolare: () => void;
}) {
  const nome = `${g.cognome} ${g.nome}`;
  const giaConvocato = altrove && <Badge variant="outline">Già convocato oggi</Badge>;

  // In colonna, la spunta sopra l'interruttore: la riga della rosa prende
  // già la larghezza. Tooltip su uno span: con asChild il suo data-state
  // coprirebbe quello del controllo, e un interruttore disabilitato non
  // riceve il puntatore. Le etichette si leggono quando la riga ha spazio.
  // Spunta e interruttore hanno un'area di tocco che sporge di 12 px per
  // lato: qui di 4, perché non si tocchino.
  const selettori = (
    <div className="flex shrink-0 flex-col gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex items-center gap-2">
            <Checkbox
              id={`convocato-${g.id}`}
              className="after:-inset-y-1"
              checked={convocato}
              onCheckedChange={onConvocato}
              disabled={!modifica}
              aria-label={`Convoca ${nome}`}
            />
            <Label htmlFor={`convocato-${g.id}`} className="hidden text-xs @md:inline">
              Convocato
            </Label>
          </span>
        </TooltipTrigger>
        <TooltipContent className="pointer-events-none">
          {convocato ? 'Convocato: togli dai convocati' : 'Convoca'}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex items-center gap-2">
            <Switch
              id={`titolare-${g.id}`}
              className="after:-inset-y-1"
              checked={titolare}
              onCheckedChange={onTitolare}
              disabled={!modifica || !convocato || (!titolare && completa)}
              aria-label={`Titolare ${nome}`}
            />
            <Label htmlFor={`titolare-${g.id}`} className="hidden text-xs @md:inline">
              Titolare
            </Label>
          </span>
        </TooltipTrigger>
        <TooltipContent className="pointer-events-none">
          {!convocato
            ? 'Titolare: prima va convocato'
            : titolare
              ? 'Titolare: rimetti in panchina'
              : completa
                ? `Titolari già al completo (${giocatoriInCampo})`
                : 'Metti titolare'}
        </TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <li className="@container">
      {riga ? (
        <RigaGiocatore
          riga={riga}
          chiusi={false}
          // I comandi della riga sono convocato e titolare: la scheda si apre dalla rosa.
          apribile={false}
          conVoti={conVoti}
          conMedie={conMedie}
          accanto={giaConvocato}
          azione={selettori}
        />
      ) : (
        <div className="flex h-full items-center gap-3 rounded-lg border border-border bg-card p-2.5">
          <Maglia numero={g.numero} ruolo={g.ruolo} fascia={COLORE_STATO[g.stato]} />
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-semibold break-words">{nome}</span>
            <BadgeStato stato={g.stato} />
            {g.statoSportivo && <CartellinoSportivo stato={g.statoSportivo} />}
            {giaConvocato}
          </span>
          {selettori}
        </div>
      )}
    </li>
  );
}
